// Shared pickup/drop-off default resolution for the Operations reports.
//
// The Planner guide/transport form defaults an UNSET pickup/drop-off to the
// day's accommodation (hotel) — see the web
// `find-day-siblings.ts:findDefaultLocationSiblingId` + `guide-form.tsx` seed.
// That default is a DISPLAY default: it is shown as a hint and is NOT persisted
// into `details.pickup`/`dropoff` (those stay null) nor into the item's leg
// locations. The Operations reports, however, read the item's `legs[0]`
// start/end location for pickup/drop-off, which for a never-selected pickup is
// the STALE product-seeded location (e.g. the product's own address). Result:
// the report shows a different pickup than the Planner screen.
//
// These helpers let the reports mirror the Planner default — resolve an unset
// pickup/drop-off to the SAME day-accommodation the Planner shows — so the two
// agree. Kept here (not duplicated in the api + web report modules, which each
// re-implement the trivial `siblingIdOf`) because the day-span match is the one
// piece of non-trivial logic that must not drift between the two paths.

// Minimal item shape the day grouping needs — satisfied by both the Planner
// detail items and the Operations `OperationReportItem`.
export type DaySiblingItem = {
  id: string;
  parentId: string | null;
  contentCategory: string | null;
  startTime: string | null;
  endTime: string | null;
};

const ACCOMMODATION_CATEGORY = 'Accommodation';

// YYYY-MM-DD prefix of an ISO timestamp — the planner day key. Templates use
// epoch-relative dates (`1970-01-01`, …) and the `day.date` column is often
// null, so day membership is keyed off the item start/end date prefixes.
// Mirrors `find-day-siblings.ts:dateOf`.
const dateOf = (iso: string | null): string | null => {
  if (!iso || iso.length < 10) return null;
  const prefix = iso.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(prefix) ? prefix : null;
};

// Id of the day's spanning accommodation (hotel) — the value the Planner
// defaults an unset pickup/drop-off to (`findDefaultLocationSiblingId`). The
// `subitem`'s parent (a top-level day item) dates the day; fall back to the
// subitem's own start when the parent isn't in `items`. Returns null when no
// hotel is on / spanning the day (the Planner leaves pickup empty there), so
// callers keep their existing leg fallback. Reuses the Planner's span/same-day
// detection and earliest-check-in ordering.
export const findDayAccommodationSiblingId = (
  items: DaySiblingItem[],
  subitem: Pick<DaySiblingItem, 'startTime' | 'parentId'>
): string | null => {
  const parent = subitem.parentId
    ? items.find((it) => it.id === subitem.parentId)
    : null;
  const dayDate = dateOf(parent?.startTime ?? subitem.startTime);
  if (!dayDate) return null;

  const accommodation = items
    .filter(
      (it) =>
        it.parentId == null && it.contentCategory === ACCOMMODATION_CATEGORY
    )
    .filter((it) => {
      const start = dateOf(it.startTime);
      const end = dateOf(it.endTime);
      // Spanning: a hotel whose [start, end] window contains the day (so a
      // subitem on a STAY / CHECK-OUT day still finds the hotel that checked in
      // earlier). An open-ended hotel matches only its own check-in day.
      return (
        start != null &&
        dayDate >= start &&
        (end == null ? dayDate === start : dayDate <= end)
      );
    })
    .sort((a, b) => {
      const as = a.startTime ?? '';
      const bs = b.startTime ?? '';
      return as < bs ? -1 : as > bs ? 1 : 0;
    });

  return accommodation[0]?.id ?? null;
};

/**
 * Would the referenced row appear in the Planner's pickup/drop-off dropdown for
 * this item's day? That list is the day's own rows plus the accommodation
 * spanning the day (`find-day-siblings.ts:findDaySiblings`), so a pointer at a
 * row OUTSIDE it is one the Planner form itself will not resolve — it falls back
 * to the leg instead and never rewrites the pointer, which is how a pointer at a
 * hotel from another leg of the trip survives as dead data.
 *
 * The day is one half of that test; `locationRefIsLive` below adds the version
 * and soft-delete halves and is what callers should use.
 *
 * Dates are compared as YYYY-MM-DD prefixes, matching the Planner's day key.
 */
export const locationRefIsOnItemDay = (
  referenced: Pick<DaySiblingItem, 'contentCategory' | 'startTime' | 'endTime'>,
  itemDate: string | null
): boolean => {
  const dayDate = dateOf(itemDate);
  const start = dateOf(referenced.startTime);
  if (!(dayDate && start)) return false;
  if (start === dayDate) return true;
  // Only accommodation spans days in that list; any other row has to be on the
  // day itself.
  if (referenced.contentCategory !== ACCOMMODATION_CATEGORY) return false;
  const end = dateOf(referenced.endTime);
  return end != null && dayDate >= start && dayDate <= end;
};

/**
 * Is a resolved `details.pickup` / `dropoff` POINTER still live for this item?
 *
 * The Planner's dropdown only ever offers rows of the CURRENT itinerary version
 * that are not deleted and belong to the item's day. A pointer failing any of
 * those is one the Planner form itself cannot resolve — it falls back to the leg
 * and never rewrites the pointer, which is exactly how such pointers survive as
 * dead data. Following one anyway puts a location on a screen that no other
 * surface shows (XT-659, PT66782: `details.pickup` named a hotel belonging to a
 * SUPERSEDED version while the leg correctly held the hotel of that night).
 *
 * On dev, 521 of the 1,468 rows where pointer and leg disagree fail the version
 * check and 3 more fail the day check, against 944 genuinely live ones — so this
 * gate is what separates the fix from a regression.
 */
export const locationRefIsLive = (
  referenced: Pick<
    DaySiblingItem,
    'contentCategory' | 'startTime' | 'endTime'
  > & {
    itineraryVersionId?: string | null;
    deletedOn?: unknown;
  },
  item: { itineraryVersionId?: string | null; startTime: string | null }
): boolean => {
  if (referenced.deletedOn != null) return false;
  if (referenced.itineraryVersionId !== item.itineraryVersionId) return false;
  return locationRefIsOnItemDay(referenced, item.startTime);
};

const LOCATION_REF_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The referenced row id when a `details.pickup`/`dropoff` cell is a POINTER at
 * another row on the trip, else null.
 *
 * Two shapes are in the data: the Planner's current
 * `{ kind: 'sibling', siblingId }` (`serializeSubitemLocation`) and the LEGACY
 * bare row uuid. A `custom` selection carries its own address and is not a
 * pointer. Every surface that resolves a pointer must agree on this, or they
 * disagree about the same row: the Operations report read both shapes while the
 * list read only the legacy one, so `referencedPickup` stayed null for
 * everything the Planner had written since — the report showed the hotel the
 * consultant picked and the list kept printing the address the leg had before
 * it (XT-659). One parser, three call sites.
 */
export const locationRefSiblingId = (raw: unknown): string | null => {
  if (typeof raw === 'string') {
    return LOCATION_REF_UUID_RE.test(raw) ? raw : null;
  }
  if (raw == null || typeof raw !== 'object') return null;
  const ref = raw as { kind?: unknown; siblingId?: unknown };
  if (ref.kind !== 'sibling') return null;
  return typeof ref.siblingId === 'string' &&
    LOCATION_REF_UUID_RE.test(ref.siblingId)
    ? ref.siblingId
    : null;
};

// True when a `details.pickup`/`dropoff` cell carries NO location at all: null,
// undefined, or an empty/blank string. A sibling reference, a custom object
// with real content, AND the legacy `'custom'` string discriminator are all
// NON-empty — the reports read the leg's typed address for those. Only a truly
// absent selection defaults to the day's accommodation. An empty custom object
// (`{ kind: 'custom' }` with no label/address/coords) also counts as empty,
// matching the Planner `parseLocationRef` rule.
export const isEmptyLocationRef = (raw: unknown): boolean => {
  if (raw == null) return true;
  if (typeof raw === 'string') return raw.trim() === '';
  if (typeof raw !== 'object') return true;
  const o = raw as Record<string, unknown>;
  if (o.kind === 'sibling') return false;
  return !(
    (typeof o.label === 'string' && o.label.length > 0) ||
    (typeof o.formattedAddress === 'string' && o.formattedAddress.length > 0) ||
    typeof o.latitude === 'number' ||
    typeof o.longitude === 'number'
  );
};
